import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { ServiceModuleToast } from './ServiceModuleFeedback';
import i18n from '../../i18n';
import {haptics} from './haptics';

const STORAGE_KEY = '@happy_time_tracking';
const TimeTrackingContext = createContext(null);

/**
 * Timer states: idle | running | paused | break
 * Each timer tracks: worksheetId, taskId, taskName, status,
 * startedAt, totalElapsed (ms), breakElapsed (ms),
 * segments: [{ type: 'work'|'break', start, end?, reason? }]
 */

export const TimeTrackingProvider = ({ children }) => {
    const [timers, setTimers] = useState({});
    const intervalRef = useRef(null);
    const loadedRef = useRef(false);

    // Load persisted timers on mount
    useEffect(() => {
        const load = async () => {
            try {
                const raw = await AsyncStorage.getItem(STORAGE_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    // Recalculate elapsed for any running timers
                    const now = Date.now();
                    const restored = {};
                    Object.entries(parsed).forEach(([id, timer]) => {
                        if (timer.status === 'running') {
                            const lastSegment = timer.segments[timer.segments.length - 1];
                            if (lastSegment && !lastSegment.end) {
                                const additional = now - lastSegment.start;
                                timer.totalElapsed += additional;
                                lastSegment.start = now; // Reset segment start
                            }
                        } else if (timer.status === 'break') {
                            const lastSegment = timer.segments[timer.segments.length - 1];
                            if (lastSegment && !lastSegment.end) {
                                const additional = now - lastSegment.start;
                                timer.breakElapsed += additional;
                                lastSegment.start = now;
                            }
                        }
                        restored[id] = timer;
                    });
                    setTimers(restored);
                }
            } catch (e) {
                console.warn('TimeTracking: Failed to load', e);
            }
            loadedRef.current = true;
        };
        load();
    }, []);

    // Persist timers when they change
    useEffect(() => {
        if (!loadedRef.current) return;
        AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(timers)).catch(() => { });
    }, [timers]);

    // Tick running timers every second
    useEffect(() => {
        const hasActive = Object.values(timers).some(
            t => t.status === 'running' || t.status === 'break'
        );
        if (hasActive) {
            intervalRef.current = setInterval(() => {
                setTimers(prev => {
                    const updated = { ...prev };
                    Object.keys(updated).forEach(id => {
                        const t = updated[id];
                        if (t.status === 'running') {
                            updated[id] = { ...t, totalElapsed: t.totalElapsed + 1000 };
                        } else if (t.status === 'break') {
                            updated[id] = { ...t, breakElapsed: t.breakElapsed + 1000 };
                        }
                    });
                    return updated;
                });
            }, 1000);
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [Object.values(timers).some(t => t.status === 'running' || t.status === 'break')]);

    const startTimer = useCallback((worksheetId, taskId, taskName) => {
        haptics.medium();
        const now = Date.now();
        setTimers(prev => ({
            ...prev,
            [taskId]: {
                worksheetId,
                taskId,
                taskName,
                status: 'running',
                startedAt: now,
                totalElapsed: prev[taskId]?.totalElapsed || 0,
                breakElapsed: prev[taskId]?.breakElapsed || 0,
                // How much of this timer's elapsed has already been persisted to
                // the backend work order. Preserved across resume/restart (it
                // lives in AsyncStorage with the timer) so a stop only ever
                // flushes NEW time and never double-counts already-saved time.
                flushedWorkMs: prev[taskId]?.flushedWorkMs || 0,
                flushedBreakMs: prev[taskId]?.flushedBreakMs || 0,
                segments: [
                    ...(prev[taskId]?.segments || []),
                    { type: 'work', start: now }
                ],
            }
        }));
        ServiceModuleToast.success(i18n.t('repair_timer_started'), taskName);
    }, []);

    // Record how much of a timer's elapsed (work + break, in ms) has been
    // persisted to the backend, so the next flush only sends the delta.
    const markTimerFlushed = useCallback((taskId, workMs, breakMs) => {
        setTimers(prev => {
            const timer = prev[taskId];

            if (!timer) return prev;

            return {
                ...prev,
                [taskId]: {
                    ...timer,
                    flushedWorkMs: Math.max(0, Number(workMs) || 0),
                    flushedBreakMs: Math.max(0, Number(breakMs) || 0),
                }
            };
        });
    }, []);

    const pauseTimer = useCallback((taskId) => {
        const now = Date.now();
        setTimers(prev => {
            const timer = prev[taskId];
            if (!timer || timer.status !== 'running') return prev;
            const segments = [...timer.segments];
            const last = segments[segments.length - 1];
            if (last && !last.end) {
                segments[segments.length - 1] = { ...last, end: now };
            }
            return {
                ...prev,
                [taskId]: { ...timer, status: 'paused', segments }
            };
        });
        ServiceModuleToast.info(i18n.t('repair_timer_paused'));
    }, []);

    const resumeTimer = useCallback((taskId) => {
        const now = Date.now();
        setTimers(prev => {
            const timer = prev[taskId];
            if (!timer || timer.status !== 'paused') return prev;
            return {
                ...prev,
                [taskId]: {
                    ...timer,
                    status: 'running',
                    segments: [...timer.segments, { type: 'work', start: now }]
                }
            };
        });
        ServiceModuleToast.success(i18n.t('repair_timer_resumed'));
    }, []);

    const startBreak = useCallback((taskId, reason = '') => {
        const now = Date.now();
        setTimers(prev => {
            const timer = prev[taskId];
            if (!timer || (timer.status !== 'running' && timer.status !== 'paused')) return prev;
            const segments = [...timer.segments];
            // Close current work segment
            const last = segments[segments.length - 1];
            if (last && last.type === 'work' && !last.end) {
                segments[segments.length - 1] = { ...last, end: now };
            }
            // Start break segment
            segments.push({ type: 'break', start: now, reason });
            return {
                ...prev,
                [taskId]: { ...timer, status: 'break', segments }
            };
        });
        ServiceModuleToast.info(i18n.t('repair_timer_break_started'), reason);
    }, []);

    const endBreak = useCallback((taskId) => {
        const now = Date.now();
        setTimers(prev => {
            const timer = prev[taskId];
            if (!timer || timer.status !== 'break') return prev;
            const segments = [...timer.segments];
            const last = segments[segments.length - 1];
            if (last && last.type === 'break' && !last.end) {
                segments[segments.length - 1] = { ...last, end: now };
            }
            // Start new work segment
            segments.push({ type: 'work', start: now });
            return {
                ...prev,
                [taskId]: { ...timer, status: 'running', segments }
            };
        });
        ServiceModuleToast.success(i18n.t('repair_timer_break_ended'));
    }, []);

    const stopTimer = useCallback((taskId) => {
        haptics.medium();
        const now = Date.now();
        setTimers(prev => {
            const timer = prev[taskId];
            if (!timer) return prev;
            const segments = [...timer.segments];
            const last = segments[segments.length - 1];
            if (last && !last.end) {
                segments[segments.length - 1] = { ...last, end: now };
            }
            return {
                ...prev,
                [taskId]: { ...timer, status: 'idle', segments }
            };
        });
        ServiceModuleToast.success(i18n.t('repair_timer_stopped'));
    }, []);

    const removeTimer = useCallback((taskId) => {
        setTimers(prev => {
            const next = { ...prev };
            delete next[taskId];
            return next;
        });
    }, []);

    const getActiveTimers = useCallback(() => {
        return Object.values(timers).filter(
            t => t.status === 'running' || t.status === 'paused' || t.status === 'break'
        );
    }, [timers]);

    const getTimer = useCallback((taskId) => {
        return timers[taskId] || null;
    }, [timers]);

    const startAdHocTimer = useCallback((taskName) => {
        const taskId = `adhoc-${Date.now()}`;
        startTimer(null, taskId, taskName);
        return taskId;
    }, [startTimer]);

    // Memoize the context value. Every callback below is already useCallback-
    // stable, so the value ref changes only when `timers` changes — consumers no
    // longer re-render on unrelated provider re-renders. Deps list every member
    // (getActiveTimers/getTimer already fold `timers` in), so the live 1s tick
    // still propagates exactly as before.
    const value = useMemo(() => ({
        timers,
        startTimer,
        startAdHocTimer,
        pauseTimer,
        resumeTimer,
        startBreak,
        endBreak,
        stopTimer,
        removeTimer,
        markTimerFlushed,
        getActiveTimers,
        getTimer,
    }), [
        timers,
        startTimer,
        startAdHocTimer,
        pauseTimer,
        resumeTimer,
        startBreak,
        endBreak,
        stopTimer,
        removeTimer,
        markTimerFlushed,
        getActiveTimers,
        getTimer,
    ]);

    return (
        <TimeTrackingContext.Provider value={value}>
            {children}
        </TimeTrackingContext.Provider>
    );
};

export const useTimeTracking = () => {
    const ctx = useContext(TimeTrackingContext);
    if (!ctx) {
        throw new Error('useTimeTracking must be used within TimeTrackingProvider');
    }
    return ctx;
};

// Utility: format milliseconds to display string
export const formatElapsed = (ms) => {
    const totalSec = Math.floor(ms / 1000);
    const hours = Math.floor(totalSec / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    if (hours > 0) {
        return `${hours}h ${String(minutes).padStart(2, '0')}m`;
    }
    return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

export default TimeTrackingContext;
